// ABOUTME: REST API wrapper for workflow management endpoints.
// ABOUTME: Provides typed functions for workflow CRUD, validation, and Mermaid generation.

const WORKFLOW_BASE_URL = '/api/workflow';

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
}

async function workflowFetch<T>(path: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${WORKFLOW_BASE_URL}${path}`, {
        headers: {
            'Content-Type': 'application/json',
            ...options?.headers,
        },
        ...options,
    });
    const json: ApiResponse<T> = await response.json();
    if (!json.success) {
        throw new Error(json.error ?? `Workflow API error: ${response.status}`);
    }
    return json.data as T;
}

export interface WorkflowDefinition {
    code: string;
    name: string;
    description?: string;
    initialState: string;
    states: {
        code: string;
        name: string;
        terminal?: boolean;
        // Chip color and icon name (see `resolveIcon`) the state is shown with.
        color?: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'success';
        icon?: string;
    }[];
    transitions: {
        event: string;
        from: string;
        to: string;
        name?: string;
        guard?: {
            allowedRoles?: string[];
            // Expression guards: JSONata evaluated against the node; must resolve to true to allow.
            conditions?: { id?: string; expression: string; message?: string }[];
        };
        autoTrigger?: { condition: string };
        // Side effect run server-side by the owning domain (e.g. convertToOrder). Generic
        // consumers cannot perform it, so they skip transitions that carry one.
        action?: { code: string; name?: string };
        // Fired only by the server-side code that owns it (an integration, the CPQ support
        // request/reply): never offered as a status button.
        system?: boolean;
    }[];
}

export interface ValidationResult {
    valid: boolean;
    errors: { path: string; message: string; code: string }[];
}

export function listWorkflows(): Promise<WorkflowDefinition[]> {
    return workflowFetch<WorkflowDefinition[]>('');
}

export function getWorkflow(code: string): Promise<WorkflowDefinition> {
    return workflowFetch<WorkflowDefinition>(`/${code}`);
}

export function saveWorkflow(def: WorkflowDefinition): Promise<void> {
    return workflowFetch<void>('', {
        method: 'PUT',
        body: JSON.stringify(def),
    });
}

export function validateWorkflow(def: WorkflowDefinition): Promise<ValidationResult> {
    return workflowFetch<ValidationResult>('/validate', {
        method: 'POST',
        body: JSON.stringify(def),
    });
}

export function getWorkflowMermaid(code: string): Promise<string> {
    return workflowFetch<string>(`/${code}/mermaid`);
}
